// src/store/slices/categorySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Category, getBranchCategories, insertCategory, updateCategory, deleteCategory } from '../../utils/supabaseClient';
import { Database } from '../../types/supabase';
import { ApiResponse } from '../../types/custom';
import { CategoryWithSubcategories } from '../../types/category';
import { setHasChanges } from './branchSlice';

// Cache duration in milliseconds (5 minutes)
const CATEGORY_CACHE_DURATION_MS = 5 * 60 * 1000;

export interface CategoryState {
	categories: Category[];
	categoriesHierarchy: CategoryWithSubcategories[],
	mainCategories: Category[];
	subCategories: Category[];
	selectedCategory: Category | null;
	isLoading: boolean;
	isCreating: boolean;
	isUpdating: boolean;
	isDeleting: boolean;
	error: string | null;
	// Cache metadata
	lastFetchedAt: number | null;
	currentBranchId: string | null;
}

const initialState: CategoryState = {
	categories: [],
	categoriesHierarchy: [],
	mainCategories: [],
	subCategories: [],
	selectedCategory: null,
	isLoading: false,
	isCreating: false,
	isUpdating: false,
	isDeleting: false,
	error: null,
	// Cache metadata
	lastFetchedAt: null,
	currentBranchId: null,
};

const buildCategoryTree = (
	rootCategory: Category,
	allSubCategories: Category[]
): CategoryWithSubcategories => {
	const children: CategoryWithSubcategories[] = allSubCategories
		.filter(category => category.parent_id === rootCategory.id)
		.map(child => buildCategoryTree(child, allSubCategories));

	return {
		...rootCategory,
		children
	};
};

// Helper function to organize categories
const organizeCategories = (categories: Category[]) => {
	const mainCategories = categories.filter(cat => !cat.parent_id);
	const subCategories = categories.filter(cat => cat.parent_id);

	const categoriesHierarchy = mainCategories.map(root => buildCategoryTree(root, subCategories));
	return { mainCategories, subCategories, categoriesHierarchy };
};

const updatePublishStatusInHierarchy = (
	categoriesHierarchy: any[] | undefined,
	categoryId: string,
	isPublished: boolean
) => {
	if (!categoriesHierarchy) {
		return;
	}

	categoriesHierarchy.forEach(category => {
		if (category.id === categoryId) {
			category.is_published = isPublished;
		}

		if (category.children && category.children.length > 0) {
			updatePublishStatusInHierarchy(category.children, categoryId, isPublished);
		}
	});
};

// Async thunks
export const fetchCategories = createAsyncThunk(
	'category/fetchCategories',
	async (params: { branchId: string; forceRefresh?: boolean }, { getState }) => {
		const { branchId, forceRefresh = false } = params;
		const state = getState() as { category: CategoryState };
		const { lastFetchedAt, currentBranchId, categories } = state.category;

		// Check if we can use cached data
		const now = Date.now();
		const isSameBranch = currentBranchId === branchId;
		const isCacheValid = lastFetchedAt && (now - lastFetchedAt) < CATEGORY_CACHE_DURATION_MS;
		const cacheAgeSeconds = lastFetchedAt ? Math.round((now - lastFetchedAt) / 1000) : null;

		if (!forceRefresh && isSameBranch && isCacheValid && categories.length > 0) {
			// Return cached data - no API call needed
			console.log(`📦 [Categories] Using CACHED data (${categories.length} items, ${cacheAgeSeconds}s old)`);
			return { data: categories, fromCache: true, branchId };
		}

		// Fetch fresh data from API
		console.log(`🔄 [Categories] Fetching FRESH data from API...`);
		const { data, error } = await getBranchCategories(branchId);
		if (error) throw error;
		console.log(`✅ [Categories] Loaded ${data?.length || 0} items from API`);
		return { data: data || [], fromCache: false, branchId };
	}
);

export const createNewCategory = createAsyncThunk(
	'category/createNewCategory',
	async (categoryData: Database["public"]["Functions"]["insert_category"]["Args"]["payload"], { dispatch }) => {
		const result = await insertCategory(categoryData);
		if (!result) throw new Error('Null result')

		// Type the result properly
		const typedResult = result as unknown as ApiResponse<Category>;
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		dispatch(setHasChanges(true));
		return typedResult.data;
	}
);

export const updateCategoryData = createAsyncThunk(
	'category/updateCategoryData',
	async (categoryData: Database["public"]["Functions"]["update_category"]["Args"]["payload"], { dispatch }) => {
		const result = await updateCategory(categoryData);
		if (!result) throw new Error('Null result')

		// Type the result properly
		const typedResult = result as unknown as ApiResponse<Category>;
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		dispatch(setHasChanges(true));
		return typedResult.data;
	}
);

export const deleteCategoryData = createAsyncThunk(
	'category/deleteCategoryData',
	async (categoryId: string, { dispatch }) => {
		const result = await deleteCategory(categoryId);
		if (!result) throw new Error('Null result')

		// Type the result properly
		const typedResult = result as unknown as ApiResponse<Category>;
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		dispatch(setHasChanges(true));
		return typedResult.data;
	}
);

const categorySlice = createSlice({
	name: 'category',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		setSelectedCategory: (state, action: PayloadAction<Category | null>) => {
			state.selectedCategory = action.payload as any;
		},
		clearCategories: (state) => {
			state.categories = [];
			state.categoriesHierarchy = [];
			state.mainCategories = [];
			state.subCategories = [];
			state.selectedCategory = null;
			// Clear cache metadata
			state.lastFetchedAt = null;
			state.currentBranchId = null;
		},
		invalidateCategoryCache: (state) => {
			// Invalidate cache to force refresh on next fetch
			state.lastFetchedAt = null;
		},
		reorderCategories: (state, action: PayloadAction<{ orderedIds: string[] }>) => {
			const { orderedIds } = action.payload;
			const categoriesArr = state.categories as unknown as Category[];
			const idToIndex = new Map(orderedIds.map((id, i) => [id, i]));

			const reordered = [...categoriesArr].sort((a, b) => {
				const ai = idToIndex.has(a.id) ? idToIndex.get(a.id)! : Infinity;
				const bi = idToIndex.has(b.id) ? idToIndex.get(b.id)! : Infinity;
				return ai - bi;
			});

			reordered.forEach((cat) => {
				if (idToIndex.has(cat.id)) cat.sort_order = idToIndex.get(cat.id)!;
			});

			state.categories = reordered as any;
			const { mainCategories, subCategories, categoriesHierarchy } = organizeCategories(reordered);
			state.mainCategories = mainCategories as any;
			state.subCategories = subCategories as any;
			state.categoriesHierarchy = [...categoriesHierarchy] as any;
		},
		toggleCategoryPublishStatus: (state, action: PayloadAction<string>) => {
			const categoryId = action.payload;
			const category = state.categories.find(cat => cat.id === categoryId);
			if (category) {
				category.is_published = !category.is_published;

				// Update in organized arrays
				const mainCategory = state.mainCategories.find(cat => cat.id === categoryId);
				if (mainCategory) {
					mainCategory.is_published = category.is_published;
				}

				const subCategory = state.subCategories.find(cat => cat.id === categoryId);
				if (subCategory) {
					subCategory.is_published = category.is_published;
				}

				// Update selected category if it matches
				if (state.selectedCategory?.id === categoryId) {
					state.selectedCategory.is_published = category.is_published;
				}

				updatePublishStatusInHierarchy(
					state.categoriesHierarchy as unknown as any[],
					categoryId,
					category.is_published
				);
			}
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch categories
			.addCase(fetchCategories.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchCategories.fulfilled, (state, action) => {
				state.isLoading = false;
				const { data, fromCache, branchId } = action.payload;

				// Only update cache metadata if we fetched fresh data
				if (!fromCache) {
					state.lastFetchedAt = Date.now();
					state.currentBranchId = branchId;
				}

				state.categories = data as any;
				const { mainCategories, subCategories, categoriesHierarchy } = organizeCategories(data);
				state.mainCategories = mainCategories as any;
				state.subCategories = subCategories as any;
				// Create new array reference to ensure re-render
				state.categoriesHierarchy = [...categoriesHierarchy] as any;
				state.error = null;
			})
			.addCase(fetchCategories.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch categories';
			})
			// Create category
			.addCase(createNewCategory.pending, (state) => {
				state.isCreating = true;
				state.error = null;
			})
			.addCase(createNewCategory.fulfilled, (state, action) => {
				state.isCreating = false;
				const updatedCategories = [...(state.categories as unknown as Category[]), action.payload];
				state.categories = updatedCategories as any;

				const { mainCategories, subCategories, categoriesHierarchy } = organizeCategories(updatedCategories);
				state.mainCategories = mainCategories as any;
				state.subCategories = subCategories as any;
				state.categoriesHierarchy = categoriesHierarchy as any;
				state.error = null;
			})
			.addCase(createNewCategory.rejected, (state, action) => {
				state.isCreating = false;
				state.error = action.error.message || 'Failed to create category';
			})
			// Update category
			.addCase(updateCategoryData.pending, (state) => {
				state.isUpdating = true;
				state.error = null;
			})
			.addCase(updateCategoryData.fulfilled, (state, action) => {
				state.isUpdating = false;
				const updatedCategory = action.payload;

				const updatedCategories = (state.categories as unknown as Category[]).map(category =>
					category.id === updatedCategory.id ? updatedCategory : category
				);
				state.categories = updatedCategories as any;

				const { mainCategories, subCategories, categoriesHierarchy } = organizeCategories(updatedCategories);
				state.mainCategories = mainCategories as any;
				state.subCategories = subCategories as any;
				state.categoriesHierarchy = categoriesHierarchy as any;

				if (state.selectedCategory?.id === updatedCategory.id) {
					state.selectedCategory = updatedCategory as any;
				}
				state.error = null;
			})
			.addCase(updateCategoryData.rejected, (state, action) => {
				state.isUpdating = false;
				state.error = action.error.message || 'Failed to update category';
			})
			// Delete category
			.addCase(deleteCategoryData.pending, (state) => {
				state.isDeleting = true;
				state.error = null;
			})
			.addCase(deleteCategoryData.fulfilled, (state, action) => {
				state.isDeleting = false;
				const deletedCategoryId = action.payload.id;

				const updatedCategories = (state.categories as unknown as Category[]).filter(category => category.id !== deletedCategoryId);
				state.categories = updatedCategories as any;

				const { mainCategories, subCategories, categoriesHierarchy } = organizeCategories(updatedCategories);
				state.mainCategories = mainCategories as any;
				state.subCategories = subCategories as any;
				state.categoriesHierarchy = categoriesHierarchy as any;

				if (state.selectedCategory?.id === deletedCategoryId) {
					state.selectedCategory = null;
				}
				state.error = null;
			})
			.addCase(deleteCategoryData.rejected, (state, action) => {
				state.isDeleting = false;
				state.error = action.error.message || 'Failed to delete category';
			});
	},
});

export const {
	clearError,
	setSelectedCategory,
	clearCategories,
	invalidateCategoryCache,
	reorderCategories,
	toggleCategoryPublishStatus
} = categorySlice.actions;
export default categorySlice.reducer;